/**
 * React Query hooks for Dashboard/Reporting API
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from './client';
import { MentionData } from 'src/hooks/useMentionUsers';
import type { DashboardTileStyle } from '../hooks/useDashboardTileStyle';
import type {
  MetricNodeCreate,
  MetricNodeUpdate,
  LevelBoundariesUpsert,
  DataValueUpsert,
  ShareDashboardRequest,
  BaseValueUpsert,
  ReminderSettingsUpdate,
  CardReminderOverride
} from './types';

// Query keys
export const queryKeys = {
  dashboards: ['dashboards'] as const,
  groupDashboards: (groupId: string) => ['groups', groupId, 'dashboards'] as const,
  dashboardCalculation: (
    id: string,
    year?: number,
    month?: number,
    numHistoricalMonths?: number,
    week?: number,
    grain?: 'week' | 'month' | 'quarter',
    tileStyle?: DashboardTileStyle
  ) => ['dashboards', id, 'calculate', year, month, week, grain, numHistoricalMonths, tileStyle] as const,
  dashboardSummary: (
    id: string,
    year?: number,
    month?: number,
    numHistoricalMonths?: number,
    week?: number,
    grain?: 'week' | 'month' | 'quarter'
  ) => ['dashboards', id, 'summary', year, month, week, grain, numHistoricalMonths] as const,
  nodePermissions: (id: string) => ['nodes', id, 'permissions'] as const,
  accessOverview: (id: string) => ['nodes', id, 'access-overview'] as const,
  metricNode: (id: string) => ['nodes', id] as const,
  levelBoundaries: (nodeId: string) => ['nodes', nodeId, 'boundaries'] as const,
  dataValues: (nodeId: string) => ['nodes', nodeId, 'data'] as const,
  baseValues: (nodeId: string) => ['nodes', nodeId, 'base-values'] as const,
  linkableCards: () => ['linkable-cards'] as const,
  linkedDashboards: (nodeId: string) => ['nodes', nodeId, 'linked-dashboards'] as const,
  availableParents: (nodeId: string) => ['nodes', nodeId, 'available-parents'] as const,
  nodeAuditLogs: (nodeId: string) => ['nodes', nodeId, 'audit-logs'] as const,
  dashboardReminders: (dashboardId: string) => ['dashboards', dashboardId, 'reminders'] as const,
  cardReminderOverride: (cardId: string) => ['nodes', cardId, 'reminder-override'] as const,
  nodeComments: (nodeId: string, year?: number, month?: number, week?: number, grain?: 'week' | 'month' | 'quarter') =>
    ['nodes', nodeId, 'comments', year, month, week, grain] as const,
  // `Array.prototype.sort` sorts in-place, so copy before sorting — the
  // caller's `nodeIds` reference is sometimes the same array used elsewhere,
  // and mutating it can produce divergent cache keys for the same logical
  // id set.
  commentCounts: (
    nodeIds: string[],
    year?: number,
    month?: number,
    week?: number,
    grain?: 'week' | 'month' | 'quarter'
  ) => ['comment-counts', year, month, week, grain, ...[...nodeIds].sort()] as const,
  importLimits: () => ['dashboards', 'import-limits'] as const
};

/**
 * The import endpoint's own upload limits, so the drop zone states a number the
 * endpoint actually enforces. Server-owned config that changes only on deploy.
 */
export function useImportLimits() {
  return useQuery({
    queryKey: queryKeys.importLimits(),
    queryFn: api.getImportLimits,
    staleTime: Infinity
  });
}

// ==================== Dashboards (Root-level GROUP_CARDs) ====================

export function useDashboards() {
  return useQuery({
    queryKey: queryKeys.dashboards,
    queryFn: api.getDashboards,
    staleTime: 0 // Always refetch — contains user_permission which must be fresh
  });
}

export function useGroupDashboards(groupId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.groupDashboards(groupId),
    queryFn: () => api.getGroupDashboards(groupId),
    enabled: !!groupId && enabled,
    staleTime: 0 // Always refetch — contains user_permission which must be fresh
  });
}

export function useLinkableCards(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.linkableCards(),
    queryFn: () => api.getLinkableCards(),
    enabled
  });
}

export function useLinkedDashboards(nodeId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.linkedDashboards(nodeId),
    queryFn: () => api.getLinkedDashboards(nodeId),
    enabled: !!nodeId && enabled
  });
}

export function useDashboardCalculation(
  dashboardId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter',
  tileStyle?: DashboardTileStyle
) {
  return useQuery({
    queryKey: queryKeys.dashboardCalculation(
      dashboardId,
      periodYear,
      periodMonth,
      numHistoricalMonths,
      periodWeek,
      periodGrain,
      tileStyle
    ),
    queryFn: () =>
      api.calculateDashboard(
        dashboardId,
        periodYear,
        periodMonth,
        numHistoricalMonths,
        periodWeek,
        periodGrain,
        tileStyle
      ),
    enabled: !!dashboardId
  });
}

export function useGroupSummary(
  groupId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  enabled: boolean = false,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
) {
  return useQuery({
    queryKey: queryKeys.dashboardSummary(
      groupId,
      periodYear,
      periodMonth,
      numHistoricalMonths,
      periodWeek,
      periodGrain
    ),
    queryFn: () => api.getGroupSummary(groupId, periodYear, periodMonth, numHistoricalMonths, periodWeek, periodGrain),
    enabled: !!groupId && enabled,
    staleTime: 60 * 1000 // Reading is cheap (no LLM call), but recomputes the dashboard
  });
}

/** Generate a new AI summary, then show the result in the summary query. */
export function useRefreshGroupSummary(
  groupId: string,
  periodYear?: number,
  periodMonth?: number,
  numHistoricalMonths?: number,
  periodWeek?: number,
  periodGrain?: 'week' | 'month' | 'quarter'
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.refreshGroupSummary(groupId, periodYear, periodMonth, numHistoricalMonths, periodWeek, periodGrain),
    onSuccess: (data) => {
      queryClient.setQueryData(
        queryKeys.dashboardSummary(groupId, periodYear, periodMonth, numHistoricalMonths, periodWeek, periodGrain),
        data
      );
    }
  });
}

// ==================== Metric Nodes ====================

export function useMetricNode(nodeId: string) {
  return useQuery({
    queryKey: queryKeys.metricNode(nodeId),
    queryFn: () => api.getMetricNode(nodeId),
    enabled: !!nodeId
  });
}

export function useCreateMetricNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MetricNodeCreate) => api.createMetricNode(data),
    onSuccess: () => {
      // Invalidate dashboards list and all calculations since structure changed
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useUpdateMetricNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: MetricNodeUpdate }) => api.updateMetricNode(nodeId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.metricNode(result.id) });
      // Invalidate dashboards list and calculations since structure changed
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useDeleteMetricNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (nodeId: string) => api.deleteMetricNode(nodeId),
    onSuccess: () => {
      // Invalidate all dashboard queries since structure changed
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Level Boundaries ====================

export function useLevelBoundaries(nodeId: string) {
  return useQuery({
    queryKey: queryKeys.levelBoundaries(nodeId),
    queryFn: () => api.getLevelBoundaries(nodeId),
    enabled: !!nodeId
  });
}

export function useUpsertLevelBoundaries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: LevelBoundariesUpsert }) =>
      api.upsertLevelBoundaries(nodeId, data),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.levelBoundaries(nodeId) });
      // Also invalidate calculations since levels affect scores
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Data Values ====================

export function useDataValues(nodeId: string) {
  return useQuery({
    queryKey: queryKeys.dataValues(nodeId),
    queryFn: () => api.getDataValues(nodeId),
    enabled: !!nodeId
  });
}

type DataValueMutationVars = {
  nodeId: string;
  data: DataValueUpsert;
  // When the caller is running a loop of mutations, suppress the
  // per-mutation invalidation cascade. The caller is expected to invalidate
  // once at the end of the batch. Without this, saving N rows caused N
  // dashboardCalculation refetches and a flickering tree underneath.
  skipInvalidation?: boolean;
};

export function useUpsertDataValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, data }: DataValueMutationVars) => api.upsertDataValue(nodeId, data),
    onSuccess: (_, { nodeId, skipInvalidation }) => {
      if (skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.dataValues(nodeId) });
      // Also invalidate calculations since data affects scores
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'groups' && query.queryKey[2] === 'recent-activity'
      });
    }
  });
}

type DataValueDeleteVars = {
  nodeId: string;
  dataValueId: string;
  skipInvalidation?: boolean;
};

export function useDeleteDataValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, dataValueId }: DataValueDeleteVars) => api.deleteDataValue(nodeId, dataValueId),
    onSuccess: (_, { nodeId, skipInvalidation }) => {
      if (skipInvalidation) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.dataValues(nodeId) });
      // Also invalidate calculations since data affects scores
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Sharing ====================

export function useNodePermissions(nodeId: string) {
  return useQuery({
    queryKey: queryKeys.nodePermissions(nodeId),
    queryFn: () => api.getNodePermissions(nodeId),
    enabled: !!nodeId,
    staleTime: 0 // Always refetch — permissions must be fresh
  });
}

/**
 * Every grant point in a node's subtree.
 *
 * Scoped to the node it is asked for, so opening it on a nested group returns
 * that group and the cards beneath it, never the dashboard above.
 */
export function useAccessOverview(nodeId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.accessOverview(nodeId ?? ''),
    queryFn: () => api.getAccessOverview(nodeId as string),
    enabled: !!nodeId && enabled,
    staleTime: 0 // Always refetch — access must be fresh
  });
}

/**
 * Drop every cached access overview after a sharing change.
 *
 * A grant on a nested card appears in the overview of every ancestor, and the
 * client cannot know which ancestors exist, so all of them go. There are at
 * most a handful cached and they refetch only while a modal is open.
 */
function invalidateAccessOverviews(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'nodes' && query.queryKey[2] === 'access-overview'
  });
}

export function useShareNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: ShareDashboardRequest }) => api.shareNode(nodeId, data),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nodePermissions(nodeId) });
      invalidateAccessOverviews(queryClient);
      // Also invalidate dashboards list since user_permission may have changed
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useRevokeNodeShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, permissionId }: { nodeId: string; permissionId: string }) =>
      api.revokeNodeShare(nodeId, permissionId),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nodePermissions(nodeId) });
      invalidateAccessOverviews(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useRevokeNodeGroupShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, groupId }: { nodeId: string; groupId: string }) => api.revokeNodeGroupShare(nodeId, groupId),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nodePermissions(nodeId) });
      invalidateAccessOverviews(queryClient);
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useUpdateNodeShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      nodeId,
      permissionId,
      data
    }: {
      nodeId: string;
      permissionId: string;
      data: { permission: number };
    }) => api.updateNodeShare(nodeId, permissionId, data),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.nodePermissions(nodeId) });
      invalidateAccessOverviews(queryClient);
      // Also invalidate dashboards list since user_permission may have changed
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Base Values ====================

export function useBaseValues(nodeId: string) {
  return useQuery({
    queryKey: queryKeys.baseValues(nodeId),
    queryFn: () => api.getBaseValues(nodeId),
    enabled: !!nodeId
  });
}

export function useUpsertBaseValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, data }: { nodeId: string; data: BaseValueUpsert }) => api.upsertBaseValue(nodeId, data),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.baseValues(nodeId) });
      // Also invalidate calculations since base values affect assurance levels
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

export function useDeleteBaseValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, baseValueId }: { nodeId: string; baseValueId: string }) =>
      api.deleteBaseValue(nodeId, baseValueId),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.baseValues(nodeId) });
      // Also invalidate calculations since base values affect assurance levels
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Move Card ====================

export function useAvailableParents(nodeId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.availableParents(nodeId),
    queryFn: () => api.getAvailableParents(nodeId),
    enabled: !!nodeId && enabled
  });
}

export function useDuplicateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (nodeId: string) => api.duplicateCard(nodeId),
    onSuccess: () => {
      // Invalidate dashboards since structure changed (new cards added)
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards });
    }
  });
}

// ==================== Activity ====================

export function useNodeAuditLogs(nodeId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.nodeAuditLogs(nodeId),
    queryFn: () => api.getNodeAuditLogs(nodeId),
    enabled: !!nodeId && enabled
  });
}

// ==================== Comments ====================

export function useNodeComments(
  nodeId: string,
  year?: number,
  month?: number,
  week?: number,
  grain?: 'week' | 'month' | 'quarter',
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.nodeComments(nodeId, year, month, week, grain),
    queryFn: () => api.getNodeComments(nodeId, year, month, week, grain),
    enabled: !!nodeId && enabled
  });
}

export function useAddComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      nodeId,
      content,
      mentions,
      year,
      month,
      week
    }: {
      nodeId: string;
      content: string;
      mentions?: MentionData[];
      year?: number;
      month?: number;
      week?: number;
    }) => api.addNodeComment(nodeId, content, mentions, year, month, week),
    onSuccess: (_, { nodeId }) => {
      queryClient.invalidateQueries({ queryKey: ['nodes', nodeId, 'comments'] });
      queryClient.invalidateQueries({ queryKey: ['comment-counts'] });
    }
  });
}

export function useReplyToComment(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, content, mentions }: { commentId: string; content: string; mentions?: MentionData[] }) =>
      api.replyToComment(commentId, content, mentions),
    onSuccess: () => {
      // Invalidate the 3-element prefix, not the full key (which now has
      // trailing year/month) — a full key with `undefined` placeholders
      // would only match that exact period's cache entry, not every period
      // cached for this node.
      queryClient.invalidateQueries({ queryKey: ['nodes', nodeId, 'comments'] });
      queryClient.invalidateQueries({ queryKey: ['comment-counts'] });
    }
  });
}

export function useEditComment(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) => api.editComment(commentId, content),
    onSuccess: () => {
      // Invalidate the 3-element prefix, not the full key (which now has
      // trailing year/month) — a full key with `undefined` placeholders
      // would only match that exact period's cache entry, not every period
      // cached for this node.
      queryClient.invalidateQueries({ queryKey: ['nodes', nodeId, 'comments'] });
      // Edits don't change the count, but the per-tile badge query also
      // surfaces modified_at — invalidate it so any timestamp-dependent UI
      // stays consistent and the prefix-match invalidation is uniform with
      // add/reply/delete.
      queryClient.invalidateQueries({ queryKey: ['comment-counts'] });
    }
  });
}

export function useDeleteComment(nodeId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) => api.deleteComment(commentId),
    onSuccess: () => {
      // Invalidate the 3-element prefix, not the full key (which now has
      // trailing year/month) — a full key with `undefined` placeholders
      // would only match that exact period's cache entry, not every period
      // cached for this node.
      queryClient.invalidateQueries({ queryKey: ['nodes', nodeId, 'comments'] });
      queryClient.invalidateQueries({ queryKey: ['comment-counts'] });
    }
  });
}

export function useCommentCounts(
  nodeIds: string[],
  year?: number,
  month?: number,
  week?: number,
  grain?: 'week' | 'month' | 'quarter',
  enabled: boolean = true
) {
  return useQuery({
    queryKey: queryKeys.commentCounts(nodeIds, year, month, week, grain),
    queryFn: () => api.getCommentCounts(nodeIds, year, month, week, grain),
    enabled: nodeIds.length > 0 && enabled
  });
}

// ==================== Reminders ====================

export function useDashboardReminders(dashboardId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.dashboardReminders(dashboardId),
    queryFn: () => api.getDashboardReminders(dashboardId),
    enabled: !!dashboardId && enabled
  });
}

export function useUpdateDashboardReminders(dashboardId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ReminderSettingsUpdate) => api.updateDashboardReminders(dashboardId, data),
    // The response is the saved state with a refreshed preview, so seed the
    // cache with it rather than round-tripping again.
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.dashboardReminders(dashboardId), settings);
    }
  });
}

export function useSetDashboardReminderMute(dashboardId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (muted: boolean) => api.setDashboardReminderMute(dashboardId, muted),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.dashboardReminders(dashboardId), settings);
    }
  });
}

export function useCardReminderOverride(cardId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.cardReminderOverride(cardId),
    queryFn: () => api.getCardReminderOverride(cardId),
    enabled: !!cardId && enabled
  });
}

export function useSetCardReminderOverride(cardId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CardReminderOverride) => api.setCardReminderOverride(cardId, data),
    onSuccess: (override) => {
      queryClient.setQueryData(queryKeys.cardReminderOverride(cardId), override);
    }
  });
}
